import type { SdkResource, SdkResourceQueryHandle } from '@Pages/pageRuntime/sdk/types';
import { createContext, useContext } from 'react';

type ResolvedResource = SdkResourceQueryHandle & Pick<SdkResource, 'capabilities'>;

/**
 * Resolves a resource id or slug to the viewer's handle, or `null` when the viewer
 * cannot access that resource. `PageRenderer` provides `Sdk.resourceOrNull`.
 */
export type SdkResourceResolver = (reference: string) => ResolvedResource | null;

const noAccess: SdkResourceResolver = () => null;

export const SdkResourceResolverContext = createContext<SdkResourceResolver>(noAccess);

/**
 * Turns a hook's resource argument into a handle. Handles with URLs pass through.
 * Strings and metadata-only handles resolve through the nearest provider. Without a
 * provider, an unresolved reference returns `null`, which keeps the hook idle.
 */
export function useResolvedResource<TResource extends Pick<SdkResource, 'id'>>(
  reference: TResource | string | null | undefined
): TResource | ResolvedResource | null {
  const resolve = useContext(SdkResourceResolverContext);

  if (typeof reference === 'string') {
    return resolve(reference);
  }

  if (!reference) {
    return null;
  }

  return 'urls' in reference ? reference : resolve(reference.id);
}
